import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ShoppingBag, Upload, Trash2, RotateCcw, Minus, Plus, Check, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { fetchProduct, api } from '@/lib/api';
import { useApp } from '@/state/AppContext';
import { inr, classNames } from '@/lib/format';
import { PRESETS, TEXT_COLORS, BG_COLORS } from '@/lib/presets';
import { AddToCartButton } from '@/components/AddToCartButton';
import { imgSrc } from '@/lib/img';

const compressUpload = (file) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error('No file'));
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const MAX = 800;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const r = Math.min(MAX / width, MAX / height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const c = document.createElement('canvas');
      c.width = width; c.height = height;
      c.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Compact card for build step
const OptionCard = ({ opt, qty, max, canInc, onInc, onDec, onSet }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(qty));

  const startEdit = () => { setDraft(String(qty || 0)); setEditing(true); };
  const commit = () => {
    const n = Math.max(0, Math.min(max, parseInt(draft, 10) || 0));
    onSet(n);
    setEditing(false);
  };
  const cancel = () => setEditing(false);

  return (
    <div className="rounded-2xl bg-white border border-[var(--bb-darkblue)]/10 overflow-hidden hover:-translate-y-0.5 transition-transform" data-testid={`option-card-${opt.id}`}>
      <div className="aspect-square bg-[var(--bb-cyan-soft)] relative flex items-center justify-center">
        {opt.image
          ? <img src={imgSrc(opt.image, 'thumb')} alt="" className="w-full h-full object-contain p-2" loading="lazy" decoding="async" />
          : <span className="font-display text-2xl text-[var(--bb-darkblue)]">{opt.label?.charAt(0)}</span>}
      </div>
      <div className="px-2 py-2">
        <div className="text-xs sm:text-sm font-nunito-black text-[var(--bb-darkblue)] text-center line-clamp-1 mb-1.5">{opt.label}</div>
        <div className="grid grid-cols-3 items-center">
          <button type="button" onClick={onDec} disabled={qty === 0} className="w-7 h-7 rounded-full bg-white border border-[var(--bb-darkblue)]/15 text-[var(--bb-darkblue)] hover:bg-[var(--bb-darkblue)]/5 disabled:opacity-30 flex items-center justify-center justify-self-start" data-testid={`option-dec-${opt.id}`}>
            <Minus className="w-3.5 h-3.5" />
          </button>
          {editing ? (
            <input
              type="number"
              min={0}
              max={max}
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
              className="w-12 text-center font-display text-lg text-[var(--bb-darkblue)] bg-[var(--bb-yellow-soft)] rounded-md border border-[var(--bb-cyan)] outline-none tabular-nums mx-auto py-0.5"
              data-testid={`option-input-${opt.id}`}
            />
          ) : (
            <button
              type="button"
              onClick={startEdit}
              className="font-display text-lg text-[var(--bb-darkblue)] text-center tabular-nums hover:bg-[var(--bb-yellow-soft)] rounded-md py-0.5 mx-auto px-1 cursor-text"
              aria-label={`Type quantity for ${opt.label}`}
              data-testid={`option-qty-${opt.id}`}
            >
              {qty}
            </button>
          )}
          <button type="button" onClick={onInc} disabled={!canInc} className="w-7 h-7 rounded-full bg-[var(--bb-cyan)] text-white hover:bg-[var(--bb-cyan-deep)] disabled:opacity-30 flex items-center justify-center justify-self-end" data-testid={`option-inc-${opt.id}`}>
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomizeFlowPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selections, setSelections] = useState({});
  const [mode, setMode] = useState('preset');
  const [presetId, setPresetId] = useState(PRESETS[0]?.id || null);
  const [uploadUrl, setUploadUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [text, setText] = useState('');
  const [textColor, setTextColor] = useState(TEXT_COLORS[0]?.value || '#1B1B1F');
  const [bgColor, setBgColor] = useState(BG_COLORS[0]?.value || '#fff7e6');
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchProduct(slug).then((p) => {
      setProduct(p);
      // start at 0 for everything; for mono boxes AND for single-option packs
      // (e.g. ChocoSnaps where the whole pack shares one design), pre-fill to pack_size
      // so the composition step can be skipped without failing the pack-complete check.
      const seed = {};
      const opts = p.pack_options || [];
      opts.forEach((o) => { seed[o.id] = 0; });
      if (opts.length === 1) {
        seed[opts[0].id] = p.pack_size;
      }
      setSelections(seed);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading || !product) {
    return <div className="max-w-6xl mx-auto px-4 py-8"><div className="animate-pulse h-[60vh] rounded-2xl bg-[var(--bb-cyan-soft)]" /></div>;
  }

  // Coming-soon products cannot be customised / purchased yet — send users to the PDP
  // where the "Coming Soon" state is already communicated.
  if (product.coming_soon) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 text-center" data-testid="customize-coming-soon">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--bb-darkblue)] text-white text-[11px] font-nunito-black uppercase tracking-wider">
          Coming Soon
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-[var(--bb-darkblue)] mt-4">{product.name}</h1>
        <p className="mt-3 text-sm text-[var(--bb-darkgrey)] font-nunito-medium">
          The Customiser for {product.category_label || 'this product'} is launching soon. Pop back later or explore what&rsquo;s live now.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Link to="/customize" className="inline-flex items-center justify-center h-11 px-5 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white font-nunito-black">
            Browse Customiser
          </Link>
          <Link to="/shop" className="inline-flex items-center justify-center h-11 px-5 rounded-xl bg-white border-2 border-[var(--bb-darkblue)] text-[var(--bb-darkblue)] font-nunito-black">
            Explore Shop
          </Link>
        </div>
      </div>
    );
  }

  // Step definition based on product type
  const STEPS = [];
  const needsComposition = product.is_pack && !product.is_mono && (product.pack_options?.length || 0) > 1;
  if (needsComposition) STEPS.push({ id: 'build', label: `Build your pack of ${product.pack_size}` });
  if (product.allow_customisation) STEPS.push({ id: 'design', label: 'Choose a Design' });
  STEPS.push({ id: 'review', label: 'Preview & Add to Cart' });

  const total = Object.values(selections).reduce((s, v) => s + (Number(v) || 0), 0);
  const remaining = product.pack_size - total;
  const packComplete = product.is_mono ? true : (total === product.pack_size);
  const current = STEPS[stepIdx];
  const canGoNext =
    current.id === 'build' ? packComplete
    : current.id === 'design' ? !!uploadUrl
    : true;
  const isLast = stepIdx === STEPS.length - 1;

  const setQty = (id, q) => setSelections({ ...selections, [id]: Math.max(0, q) });
  const inc = (id) => { if (remaining > 0) setQty(id, (selections[id] || 0) + 1); };
  const dec = (id) => setQty(id, (selections[id] || 0) - 1);
  const setExact = (id, n) => {
    // clamp to leave room for the rest of the pack
    const others = total - (selections[id] || 0);
    const allowed = Math.max(0, Math.min(product.pack_size - others, n));
    setSelections({ ...selections, [id]: allowed });
  };
  const reset = () => {
    const next = {};
    (product.pack_options || []).forEach((o) => { next[o.id] = 0; });
    setSelections(next);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large', { description: 'Max 5 MB. Try compressing it first.' });
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/upload/design', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data?.url) {
        setUploadUrl(data.url);
        toast.success('Design uploaded');
      } else {
        throw new Error('Upload did not return a URL');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not upload';
      toast.error('Upload failed', { description: msg });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const buildCustomisationPayload = () => {
    if (!product.allow_customisation) return null;
    if (!uploadUrl) return null;
    return {
      mode: 'upload',
      design_url: uploadUrl,           // Cloudinary CDN URL — sent to Shopify as a line-item property
      preview_data_url: uploadUrl,     // used by cart / checkout thumbnails
    };
  };

  const finish = (goCheckout = false) => {
    if (!packComplete) { toast.error(`Pick ${product.pack_size} ${product.pack_unit}`); return false; }
    if (product.allow_customisation && !uploadUrl) {
      toast.error('Upload an image first', { description: 'Choco Snaps need a design to print.' });
      return false;
    }
    addToCart({
      product_id: product.id, product_slug: product.slug, name: product.name,
      image: product.image, price: product.price, qty: 1, variant: product.variant,
      customization: buildCustomisationPayload(),
      pack: { pack_size: product.pack_size, pack_unit: product.pack_unit, selections: { ...selections } },
      customised: true,
    });
    toast.success(`${product.name} added to cart`);
    // Defer navigation until success state of the morph button is visible
    setTimeout(() => navigate(goCheckout ? '/checkout' : '/cart'), 1150);
    return true;
  };

  const goToStep = (idx) => {
    if (idx < 0 || idx >= STEPS.length) return;
    // can jump only to current or earlier steps
    if (idx > stepIdx) return;
    setStepIdx(idx);
  };

  const next = () => {
    if (!canGoNext) { toast.error(`Pack must total ${product.pack_size}`); return; }
    if (isLast) finish(false); else setStepIdx(stepIdx + 1);
  };

  const pct = product.pack_size > 0 ? Math.min(100, (total / product.pack_size) * 100) : 0;

  // Build composition tags (used in review header)
  const compositionTags = (product.pack_options || []).map((o) => ({ id: o.id, label: o.label, qty: selections[o.id] || 0 })).filter((t) => t.qty > 0);

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex flex-col pb-24 md:pb-20" data-testid="customize-flow-page">
      {/* Top: back + hero header */}
      <section className="max-w-6xl mx-auto w-full px-4 md:px-6 pt-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-nunito-bold text-[var(--bb-darkgrey)] hover:text-[var(--bb-darkblue)]" data-testid="flow-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-xs font-nunito-black uppercase tracking-wider text-[var(--bb-darkgrey)] mt-3">Customise · {product.category_label}</div>
        <div className="mt-2 grid md:grid-cols-[1fr_auto] gap-4 items-end">
          <div className="flex items-end gap-3">
            <img src={imgSrc(product.image, 'thumb')} alt="" className="w-16 h-16 md:w-24 md:h-24 object-contain rounded-2xl bg-white border border-[var(--bb-darkblue)]/10 shrink-0" loading="lazy" decoding="async" />
            <div>
              <h1 className="font-display text-2xl md:text-3xl text-[var(--bb-darkblue)] leading-[0.95]">{product.category_label}</h1>
              <div className="font-display text-lg md:text-xl text-[var(--bb-pink)] mt-0.5">
                {current.id === 'review' ? 'Preview your pack' : current.id === 'design' ? 'Choose a design' : product.is_mono ? 'Customise your locked pack' : `Build your own pack of ${product.pack_size}`}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-nunito-bold text-[var(--bb-darkgrey)] uppercase tracking-wider">Pack Price</div>
            <div className="font-display text-xl md:text-2xl text-[var(--bb-darkblue)]">{inr(product.price)}</div>
          </div>
        </div>

        {/* Composition image cards (visible on review step) */}
        {current.id === 'review' && compositionTags.length > 0 && (
          <div className="mt-4" data-testid="review-tags">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-nunito-black uppercase tracking-wider text-[var(--bb-darkgrey)]">Your pack composition</span>
              {needsComposition && (
                <button onClick={() => goToStep(STEPS.findIndex((s) => s.id === 'build'))} className="inline-flex items-center gap-1 text-xs font-nunito-black text-[var(--bb-cyan)] hover:text-[var(--bb-cyan-deep)]" data-testid="review-edit-pack">
                  <Edit3 className="w-3 h-3" /> Edit pack
                </button>
              )}
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="review-cards">
              {compositionTags.map((t) => {
                const opt = (product.pack_options || []).find((o) => o.id === t.id) || {};
                return (
                  <div key={t.id} className="relative rounded-xl bg-white border border-[var(--bb-darkblue)]/10 overflow-hidden" data-testid={`review-card-${t.id}`}>
                    <div className="aspect-square bg-[var(--bb-cyan-soft)] relative">
                      {opt.image
                        ? <img src={imgSrc(opt.image, 'avatar')} alt={t.label} className="w-full h-full object-contain p-1.5" loading="lazy" decoding="async" />
                        : <span className="absolute inset-0 flex items-center justify-center font-display text-2xl text-[var(--bb-darkblue)]">{t.label?.charAt(0)}</span>}
                      <div className="absolute top-1 right-1 min-w-7 h-6 px-1.5 rounded-full bg-[var(--bb-pink)] text-white text-xs font-nunito-black flex items-center justify-center">
                        ×{t.qty}
                      </div>
                    </div>
                    <div className="px-1.5 py-1 text-[11px] font-nunito-black text-[var(--bb-darkblue)] text-center truncate">{t.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Progress (only on build step) */}
      {current.id === 'build' && (
        <section className="max-w-6xl mx-auto w-full px-4 md:px-6 mt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-nunito-bold text-[var(--bb-darkgrey)]">
              <span className="font-nunito-black text-[var(--bb-darkblue)]" data-testid="flow-total">{total}</span> / {product.pack_size} {product.pack_unit}
            </div>
            <button type="button" onClick={reset} className="inline-flex items-center gap-1 text-sm text-[var(--bb-darkgrey)] hover:text-[var(--bb-darkblue)] font-nunito-bold" data-testid="flow-reset">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-[var(--bb-darkblue)]/10 overflow-hidden">
            <div className="h-full bg-[var(--bb-cyan)] transition-[width] duration-200" style={{ width: `${pct}%` }} />
          </div>
        </section>
      )}

      {/* Step body — flex-1 so it fills viewport */}
      <section className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 mt-4">
        {current.id === 'build' && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5 md:gap-3" data-testid="flow-step-build">
            {(product.pack_options || []).map((opt) => {
              const q = selections[opt.id] || 0;
              const otherTotal = total - q;
              const optMax = Math.max(0, product.pack_size - otherTotal);
              return (
                <OptionCard
                  key={opt.id}
                  opt={opt}
                  qty={q}
                  max={optMax}
                  canInc={remaining > 0}
                  onInc={() => inc(opt.id)}
                  onDec={() => dec(opt.id)}
                  onSet={(n) => setExact(opt.id, n)}
                />
              );
            })}
          </div>
        )}

        {current.id === 'design' && (
          <div className="space-y-5 max-w-2xl" data-testid="flow-step-design">
            <div>
              <h3 className="font-display text-2xl md:text-3xl text-[var(--bb-darkblue)]">Upload your design</h3>
              <p className="mt-1 text-sm text-[var(--bb-darkgrey)] font-nunito-medium">
                We&rsquo;ll print your image on every ChocoSnap in the pack. JPG or PNG, ideally square, up to 5 MB.
              </p>
            </div>

            {uploadUrl ? (
              <div className="rounded-2xl bg-white border border-[var(--bb-darkblue)]/15 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="relative w-40 h-40 sm:w-32 sm:h-32 rounded-2xl overflow-hidden bg-[var(--bb-sky-soft)] flex-shrink-0">
                  <img src={uploadUrl} alt="Your uploaded design" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-nunito-black text-[var(--bb-darkblue)]">Looks great! 🎉</div>
                  <div className="text-sm text-[var(--bb-darkgrey)] mt-0.5">Your image will appear on every ChocoSnap in this pack.</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-nunito-black bg-[var(--bb-cyan)] text-white cursor-pointer hover:bg-[var(--bb-cyan-deep)]" data-testid="design-upload-replace">
                      <Upload className="w-3.5 h-3.5" /> Replace
                      <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
                    </label>
                    <button
                      type="button"
                      onClick={() => { setUploadUrl(null); }}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-nunito-black bg-white border border-[var(--bb-darkblue)]/15 text-[var(--bb-darkblue)] hover:bg-[var(--bb-sky-soft)]"
                      data-testid="design-upload-remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <label
                className="block relative rounded-2xl border-2 border-dashed border-[var(--bb-cyan)]/60 bg-[var(--bb-cyan-soft)] hover:bg-[var(--bb-cyan)]/15 transition-colors cursor-pointer p-8 text-center"
                data-testid="design-upload-dropzone"
              >
                <div className="mx-auto w-14 h-14 rounded-full bg-white grid place-items-center bb-shadow-soft">
                  <Upload className="w-6 h-6 text-[var(--bb-cyan-deep)]" />
                </div>
                <div className="mt-3 font-nunito-black text-[var(--bb-darkblue)]">Tap to upload your image</div>
                <div className="mt-1 text-xs text-[var(--bb-darkgrey)] font-nunito-medium">JPG or PNG · Recommended 1200 × 1200 px · Max 5 MB</div>
                <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="design-upload-input" />
              </label>
            )}
          </div>
        )}

        {current.id === 'review' && (
          <div className="max-w-3xl" data-testid="flow-step-review">
            {product.allow_customisation && (
              <div className="mt-2">
                <div className="text-xs font-nunito-black uppercase tracking-wider text-[var(--bb-darkgrey)]">Your design</div>
                <div className="mt-2 flex items-center gap-3">
                  {uploadUrl ? (
                    <>
                      <img src={uploadUrl} alt="Your uploaded design" className="w-20 h-20 rounded-xl object-cover bg-[var(--bb-sky-soft)] border border-[var(--bb-darkblue)]/10" />
                      <div className="flex-1">
                        <div className="text-sm font-nunito-black text-[var(--bb-darkblue)]">Custom design uploaded ✓</div>
                        <button
                          onClick={() => goToStep(STEPS.findIndex((s) => s.id === 'design'))}
                          className="inline-flex items-center gap-1 text-xs font-nunito-black text-[var(--bb-cyan)] hover:text-[var(--bb-cyan-deep)] mt-1"
                          data-testid="review-edit-design"
                        >
                          <Edit3 className="w-3 h-3" /> Replace image
                        </button>
                      </div>
                    </>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[var(--bb-pink-soft)] text-[var(--bb-pink-deep)] text-xs font-nunito-black">
                      No design uploaded yet
                    </span>
                  )}
                </div>
              </div>
            )}
            <p className="mt-4 text-sm font-nunito-medium text-[var(--bb-darkgrey)]">All set? Tap <span className="font-nunito-black text-[var(--bb-darkblue)]">Add to Cart</span> below to save your pack. We will craft it just for you and ship to select locations.</p>
          </div>
        )}
      </section>

      {/* Sticky cyan footer — fixed to bottom of screen (above mobile bottom-nav, flush on desktop) */}
      <div
        className="fixed left-0 right-0 z-40 bg-[var(--bb-cyan)] text-white border-t border-white/20 bottom-16 md:bottom-0 md:[left:var(--bb-sidenav-w,240px)]"
        data-testid="flow-sticky-footer"
      >
        <div className="max-w-6xl mx-auto px-3 md:px-6 py-2.5 flex items-center gap-3">
          <ol className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-none" data-testid="flow-stepper">
            {STEPS.map((s, i) => {
              const done = i < stepIdx;
              const active = i === stepIdx;
              const clickable = i <= stepIdx;
              return (
                <li key={s.id} className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => goToStep(i)}
                    disabled={!clickable}
                    className={classNames(
                      'flex items-center gap-2 transition-colors',
                      clickable ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed opacity-80',
                    )}
                    data-testid={`flow-step-${s.id}`}
                    aria-label={s.label}
                  >
                    <span className={classNames('w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-nunito-black', active ? 'bg-white text-[var(--bb-cyan)] border-white' : done ? 'bg-[var(--bb-green)] border-[var(--bb-green)] text-white' : 'bg-transparent text-white border-white/70')}>
                      {done ? <Check className="w-4 h-4" /> : (i + 1)}
                    </span>
                    <span className={classNames('text-[11px] md:text-xs font-nunito-bold whitespace-nowrap', active ? 'text-white' : 'text-white/80')}>{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <span className="w-3 sm:w-6 h-px bg-white/40" />}
                </li>
              );
            })}
          </ol>
          {isLast ? (
            <AddToCartButton
              onAdd={() => finish(false)}
              label="Add to Cart"
              successLabel="Added!"
              variant="white"
              className="!h-10 !text-sm"
              data-testid="flow-next"
            />
          ) : (
            <button
              onClick={next}
              disabled={!canGoNext}
              className="h-10 px-5 rounded-xl bg-white text-[var(--bb-darkblue)] font-nunito-black hover:bg-white/95 disabled:opacity-50 inline-flex items-center"
              data-testid="flow-next"
            >
              NEXT <ArrowRight className="w-4 h-4 ml-1" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomizeFlowPage;
